import { useEffect, useState } from 'react';
import { useEventDetails } from '../../hooks/useEventDetails';
import { useEventAccess } from '../../hooks/useEventAccess';
import { RestrictedEventAccess } from '../../components/RestrictedEventAccess';

interface EventAvailabilityGuardProps {
  children: React.ReactNode;
  eventSlug: string;
}

export const EventAvailabilityGuard: React.FC<EventAvailabilityGuardProps> = ({ children, eventSlug }) => {
  const { data: eventDetails, isLoading: isLoadingEvent, error: eventError } = useEventDetails(eventSlug);
  const { data: accessCheck, isLoading: isCheckingAccess, error: accessError } = useEventAccess(eventSlug);
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    // Check if we got a 404 error indicating event is not available
    if (eventError && !hasRedirected) {
      const isNotFound = 
        (eventError as any)?.response?.status === 404 ||
        (eventError as any)?.message?.includes('404') ||
        (eventError as any)?.message?.includes('not found') ||
        (eventError as any)?.message?.includes('not available');

      if (isNotFound) {
        setHasRedirected(true);
        
        // Show alert to user
        alert('This event could not be found or is no longer active.');
        
        // Redirect to root
        window.location.href = '/';
      }
    }
  }, [eventError, hasRedirected]);

  const isLoading = isLoadingEvent || isCheckingAccess;

  // Show loading state while checking
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event...</p>
        </div>
      </div>
    );
  }

  // If we have an error but haven't redirected yet, show nothing (will redirect soon)
  if (eventError && !hasRedirected) {
    return null;
  }

  // If access check is available and access is denied, show restriction component
  if (accessCheck && !accessCheck.hasAccess) {
    return (
      <RestrictedEventAccess
        eventSlug={eventSlug}
        accessCheck={accessCheck}
        primaryColor={eventDetails?.style?.headerColor}
        buttonBgColor={eventDetails?.style?.buttonBgColor}
        buttonTextColor={eventDetails?.style?.buttonTextColor}
        eventName={eventDetails?.team?.eventName || 'event'}
      >
        {children}
      </RestrictedEventAccess>
    );
  }

  // Event is available and access is granted, render children
  return <>{children}</>;
};